import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ApplicationState } from 'store';
import { AcademyState, AcademyLive } from 'store/modules/academy_store/types';
import { academyMonthKey, academyMonthLabel } from 'services/academy_date';
import Loader from 'components/atoms/loader';
import AcademyButton from 'components/atoms/academy_button';
import AcademyFeaturedEvent from 'components/molecules/academy_featured_event';
import AcademyEventRow from 'components/molecules/academy_event_row';
import AcademySubscriptionModal from 'components/molecules/academy_subscription_modal';
import SubscribedIcon from 'assets/images/icon_starred_seet_blue.svg';
import { Container } from './styles';

const AcademyAgenda: React.FC = () => {
  const history = useHistory();
  const { agenda, mySubscriptions, request } = useSelector<ApplicationState, AcademyState>(
    (s) => s.session.academy,
  );

  const [modalLive, setModalLive] = useState<AcademyLive | null>(null);

  const subscribedIds = useMemo(
    () => new Set(mySubscriptions.map((sub) => sub.live_id)),
    [mySubscriptions],
  );

  const groupedByMonth = useMemo(() => {
    const rest = agenda.slice(1);
    const groups: { key: string; label: string; items: AcademyLive[] }[] = [];
    rest.forEach((live) => {
      const key = academyMonthKey(live.scheduled_at);
      let group = groups.find((g) => g.key === key);
      if (!group) {
        group = { key, label: academyMonthLabel(live.scheduled_at), items: [] };
        groups.push(group);
      }
      group.items.push(live);
    });
    return groups;
  }, [agenda]);

  const actionFor = (live: AcademyLive) => {
    if (live.status === 'live') {
      return (
        <AcademyButton icon="live_tv" onClick={() => history.push('/academy/ao-vivo')}>
          Assistir ao vivo
        </AcademyButton>
      );
    }
    if (subscribedIds.has(live.id)) {
      return (
        <AcademyButton variant="outline" imgIcon={SubscribedIcon}>
          Inscrito
        </AcademyButton>
      );
    }
    return (
      <AcademyButton icon="event" onClick={() => setModalLive(live)}>
        Inscrever-se
      </AcademyButton>
    );
  };

  if (request.agenda.loading) {
    return (
      <Container>
        <div className="loading-wrapper">
          <Loader />
        </div>
      </Container>
    );
  }

  if (agenda.length === 0) {
    return (
      <Container>
        <div className="empty-state">
          <i className="material-icons">event_busy</i>
          <p>Nenhuma transmissão agendada no momento.</p>
        </div>
      </Container>
    );
  }

  const featured = agenda[0];

  return (
    <Container className="container">
      <div className="section-label">Próximo evento</div>
      <AcademyFeaturedEvent
        live={featured}
        action={actionFor(featured)}
        onOpen={() => history.push(`/academy/calendario/${featured.id}`)}
      />

      {groupedByMonth.map((group) => (
        <div key={group.key}>
          <div className="month-label">{group.label}</div>
          {group.items.map((live) => (
            <AcademyEventRow
              key={live.id}
              live={live}
              action={actionFor(live)}
              onOpen={() => history.push(`/academy/calendario/${live.id}`)}
            />
          ))}
        </div>
      ))}

      <AcademySubscriptionModal
        show={Boolean(modalLive)}
        live={modalLive}
        onClose={() => setModalLive(null)}
      />
    </Container>
  );
};

export default AcademyAgenda;
